/* eslint-disable camelcase */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import './Recommendation.scss';
import ShowHide from '../ShowHide/ShowHide';
import { LocaleContext } from '../../contexts';
import getText from '../../assets/locale/locale';
import addDataLayerTrigger from '../../utils/addDataLayerTrigger/addDataLayerTrigger';

const Recommendation = ({ rec, startExpanded, index, createRank }) => {
  const {
    affects_me_text, // eslint-disable-line camelcase
    desc,
    fa_text, // eslint-disable-line camelcase
    icon,
    learn_more_link, // eslint-disable-line camelcase
    learn_more_text, // eslint-disable-line camelcase
    title,
    seo_links,
    disclaimer,
  } = rec;
  const locId = useContext(LocaleContext);
  const iconURL = `/public/icons/${icon}.png`;
  const [rank, setRank] = useState(null);

  useEffect(() => {
    setRank(createRank());
  }, []);

  /* Info for overriding Material-UI styles with css classes:
   https://material-ui.com/customization/components/#overriding-styles-with-global-class-names */

  return (
    <Accordion
      defaultExpanded={startExpanded}
      classes={{ root: 'expansion-panel' }}
      onChange={(e, expanded) => {
        if (expanded) {
          addDataLayerTrigger('rec-category-click', {
            'result-clicked': {
              title,
              score: index,
              rank,
            },
          });
        }
      }}
    >
      <AccordionSummary
        classes={{ root: 'expansion-panel-header' }}
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel${index}-content`}
        id={`${index}-header`}
      >
        <img
          src={iconURL}
          alt=""
          className="expansion-panel-icon"
          role="presentation"
        />
        <p>{title}</p>
      </AccordionSummary>
      <AccordionDetails classes={{ root: 'expansion-panel-content' }}>
        <ShowHide
          prompt={getText(locId, 'Results', 'firstPrompt', 'Recommendation')}
          content={desc}
          bulleted={false}
        />
        <ShowHide
          prompt={getText(locId, 'Results', 'secondPrompt', 'Recommendation')}
          content={affects_me_text}
          bulleted
        />
        <ShowHide
          prompt={getText(locId, 'Results', 'thirdPrompt', 'Recommendation')}
          content={fa_text}
          bulleted
        />
        <a
          className="learn-more"
          href={learn_more_link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => addDataLayerTrigger('learn-more-click')}
        >
          {learn_more_text}
        </a>
        {seo_links && seo_links.length > 0 ? (
          <div className="seo-links-container">
            <h4>{getText(locId, 'Results', 'resources', 'Recommendation')}</h4>
            <ul>
              {seo_links.map((link) => (
                <li key={link.seo_text}>
                  <a
                    className="seo-link"
                    href={link.seo_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.seo_text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {disclaimer && disclaimer.length > 0 ? (
          <div className="disclaimer-container">{disclaimer}</div>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
};

Recommendation.propTypes = {
  rec: PropTypes.shape({
    affects_me_text: PropTypes.arrayOf(PropTypes.string).isRequired,
    desc: PropTypes.string.isRequired,
    fa_text: PropTypes.arrayOf(PropTypes.string).isRequired,
    icon: PropTypes.string.isRequired,
    learn_more_link: PropTypes.string.isRequired,
    learn_more_text: PropTypes.string.isRequired,
    seo_links: PropTypes.arrayOf(
      PropTypes.shape({
        seo_text: PropTypes.string.isRequired,
        seo_url: PropTypes.string.isRequired,
      })
    ),
    title: PropTypes.string.isRequired,
    disclaimer: PropTypes.string,
  }).isRequired,
  startExpanded: PropTypes.bool.isRequired,
  index: PropTypes.string.isRequired,
  createRank: PropTypes.func.isRequired,
};

export default Recommendation;
